import { useEffect, useState } from "react";
import Loader from "react-loader-spinner";
import { toast } from "react-toastify";
import Select from "react-select";
import BankBeneficiariesModal from "../BeneficiariesModal/BankBeneficiariesModal";
import {
  areFieldsSet,
  beneficiarySelection,
  chooseBeneficiary,
  fetchBanks,
  fetchBeneficiaries,
  handleChange,
  verifyAccountNumber,
} from "./BankTransfterUtils";
import { isCommissionWallet } from "../OtherWallet/InitiateTransfer";

function InitiateTransfer({
  handleInitiateTransfer,
  handleChangeTransferDetails,
  user,
  transferDetails,
  setTransferDetails,
  bankAccountNumber,
  bankAccountName,
  setBankAccountName,
  setAccountDetails,
  saveBeneficiary,
  handleSaveBeneficiary,
  showAlert,
  setShowAlert,
}) {
  const [banks, setBanks] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const senderAccount = user.wallets[0].account_no;
  const [showModal, setShowModal] = useState(false);
  const [beneficiarySelected, setBeneficiarySelected] = useState(false);
  const [isExistingBeneficiary, setIsExistingBeneficiary] = useState(false);

  // Alphabetically sort the banks
  const sortedBanks = banks.slice().sort((a, b) => {
    const bankNameA = a.name.toUpperCase();
    const bankNameB = b.name.toUpperCase();

    if (bankNameA < bankNameB) {
      return -1;
    }
    if (bankNameA > bankNameB) {
      return 1;
    }
    return 0;
  });

  // Convert banks array to array of objects format required by react-select
  const bankOptions = sortedBanks.map((bank) => ({
    // value: bank.code,
    value: `${bank.code}***${bank.name}`,
    label: bank.name,
    bankName: bank.name,
  }));

  // Handle bank change to populate the form with the selected beneficiary details.
  const handleBankChange = (selectedOption) => {
    handleChange(
      selectedOption,
      handleChangeTransferDetails,
      setBankAccountName,
      setTransferDetails,
      transferDetails
    );
  };

  const handleVerifyAccountNo = () => {
    verifyAccountNumber(
      setBankAccountName,
      setAccountDetails,
      setTransferDetails,
      transferDetails,
      setIsValidating,
      bankAccountNumber,
      beneficiaries,
      setIsExistingBeneficiary,
      toast
    );
  };

  // Integrate fetching beneficiary into the handleChooseBeneficiary function.
  const handleChooseBeneficiary = async () => {
    chooseBeneficiary(
      setBeneficiaries,
      setShowModal,
      senderAccount,
      bankOptions
    );
  };

  const handleBeneficiarySelection = async (beneficiary) => {
    beneficiarySelection(
      beneficiary,
      setTransferDetails,
      setBankAccountName,
      setShowModal,
      setBeneficiarySelected,
      toast,
      transferDetails
    );
  };

  const fieldsAreSet = areFieldsSet(transferDetails, bankAccountNumber);

  useEffect(() => {
    fetchBanks(setBanks);
    fetchBeneficiaries(senderAccount, setBeneficiaries);
  }, [senderAccount]);

  // Automatically validate account number of saved beneficiary when the component renders.
  useEffect(() => {
    if (
      beneficiaries.length > 0 &&
      transferDetails.bank &&
      transferDetails.account_number
    ) {
      // Find the saved beneficiary
      const savedBeneficiary = beneficiaries.find(
        (beneficiary) =>
          beneficiary.recipient_account_number ===
          transferDetails.account_number
      );
      if (savedBeneficiary) {
        verifyAccountNumber(
          setBankAccountName,
          setAccountDetails,
          setTransferDetails,
          transferDetails,
          setIsValidating,
          bankAccountNumber,
          beneficiaries,
          setIsExistingBeneficiary,
          toast
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaries, transferDetails.bank, transferDetails.account_number]);

  return (
    <>
      <h2 className="mb-5 text-primary fw-bold">
        Initiate Wallet-to-Bank Transfer
      </h2>
      <div className="card shadow h-100 py-2">
        <div className="card-body">
          <h3 className="card-title text-muted">
            Transfer Funds to a Bank Account
          </h3>
          <form onSubmit={handleInitiateTransfer}>
            <div className="form-group mb-3">
              <label
                className="form-label text-secondary"
                htmlFor="walletAccountNumber"
              >
                From Wallet
              </label>
              <select
                className="form-control"
                name="wallet_account_number"
                id="walletAccountNumber"
                onChange={handleChangeTransferDetails}
              >
                <option value="">--Select Wallet--</option>
                {/* Exclude Savings wallet as it is not a transactable */}
                {user.wallets
                  .filter(
                    (wallet) =>
                      !isCommissionWallet(wallet.product_id) &&
                      wallet.type !== "savings",
                  )
                  .map((wallet) => (
                    <option value={wallet.account_no} key={wallet.id}>
                      {wallet.name} (&#8358;
                      {Number(wallet.balance).toFixed(2).toLocaleString()})
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <label
                  className="form-label text-secondary"
                  htmlFor="receivingBank"
                >
                  To Bank
                </label>
                <button
                  type="button"
                  className="btn btn-link fw-bolder text-decoration-none"
                  onClick={handleChooseBeneficiary}
                >
                  Choose Beneficiary
                </button>
              </div>
              <Select
                className="mb-5"
                options={bankOptions}
                // Select bank from saved beneficiary.
                value={
                  transferDetails.bank
                    ? {
                        value: transferDetails.bank,
                        label: transferDetails.bankName,
                      }
                    : null
                }
                onChange={handleBankChange}
                placeholder="Select Receiving Bank..."
              />
            </div>

            {showModal && (
              <BankBeneficiariesModal
                beneficiaries={beneficiaries}
                setBeneficiaries={setBeneficiaries}
                handleBeneficiarySelection={handleBeneficiarySelection}
                showAlert={showAlert}
                setShowAlert={setShowAlert}
                showModal={showModal}
                setShowModal={setShowModal}
                banks={banks}
              />
            )}

            <div className="form-group mb-3">
              <label
                className="form-label text-secondary"
                htmlFor="bankAccountNumber"
              >
                Account Number
              </label>
              <input
                type="text"
                className="form-control mb-5"
                name="account_number"
                value={bankAccountNumber || transferDetails.account_number}
                id="bankAccountNumber"
                placeholder="Receiving Account Number (NUBAN)"
                onChange={handleChangeTransferDetails}
                required
                title="Numbers only"
                maxLength="10"
              />
              <div className="mt-2">
                {!isValidating ? (
                  <span
                    className="btn btn-primary-soft"
                    onClick={handleVerifyAccountNo}
                  >
                    Validate
                  </span>
                ) : (
                  <span className="btn btn-primary-soft">
                    <Loader type="Oval" color="#fff" width={20} height={20} />
                  </span>
                )}
              </div>
            </div>
            <div className="form-group mb-3">
              <input
                className="form-control mb-5"
                type="text"
                value={bankAccountName}
                disabled
              />
            </div>

            <div className="form-group mb-5">
              <label
                className="form-label text-secondary"
                htmlFor="transferAmount"
              >
                Amount &#8358;
              </label>

              <input
                name="amount"
                id="transferAmount"
                type="number"
                className="form-control mb-5"
                placeholder=""
                title="Numbers only"
                maxLength="10"
                required
                onChange={handleChangeTransferDetails}
                step="0.01" // Accommodate for decimal values
                onWheel={(e) => e.target.blur()} //Disable scrolling
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label text-secondary" htmlFor="narration">
                Narration (Optional)
              </label>

              <input
                name="narration"
                id="narration"
                type="text"
                className="form-control mb-5"
                placeholder=""
                maxLength="30"
                onChange={handleChangeTransferDetails}
              />
            </div>
            <div className="form-check mb-5">
              {!isExistingBeneficiary && !beneficiarySelected && (
                <>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="saveBeneficiaryCheckbox"
                    aria-label="Save Beneficiary"
                    aria-checked={false}
                    checked={saveBeneficiary}
                    onChange={() => {
                      handleSaveBeneficiary();
                    }}
                  />
                  <label
                    htmlFor="saveBeneficiaryCheckbox"
                    className="form-check-label"
                  >
                    Save Beneficiary
                  </label>
                </>
              )}
            </div>

            <button
              type="submit"
              className="btn w-100 btn-primary mt-5"
              disabled={!fieldsAreSet}
            >
              Proceed
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default InitiateTransfer;
